import React from 'react'
import Route from 'route-parser'
import { MAILTO } from 'libraries/front-end-core/constants/urls'
import { URL_PDF_CA } from 'constants/urls'
import url from 'libraries/front-end-core/core/url'
import useObservable from 'libraries/front-end-core/hooks/useObservable'
import { isValidResponse } from 'libraries/front-end-core/helpers/dataflow'
import { getBaseURI } from 'libraries/front-end-core/helpers/document'
import { SHARE } from 'constants/controllers'
import cx from 'classnames'
import { Popover, PopoverButton, PopoverPanel } from '@headlessui/react'
import Icon from 'components/shared/Icon'
import { toast } from 'react-toastify'
import notificationToast from 'helpers/notificationToast'
import { Link } from 'lucide-react'
import TealToolTip from 'components/TealToolTip'
import { htmlText } from 'helpers/htmlText'
import { LinkedinShareButton, LinkedinIcon } from 'react-share'

const mailParser = new Route(MAILTO)

const ShareButton = ({ articleLink }) => {
  return (
    <Popover className='relative'>
      <TealToolTip title='Share' placement='top' arrow={false} bgColor='dark'>
        <PopoverButton className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue'>
          <Icon name='icon-share-variant' />
        </PopoverButton>
      </TealToolTip>
      <PopoverPanel className='absolute z10'>
        <div className='flex gap5 share-buttons-container bg-white shadow-1 br2 pa3'>
          <LinkedInButton link={articleLink} />
          <CopyLinkToClipboardButton link={articleLink} dataClipboardText={articleLink} />
        </div>
      </PopoverPanel>
    </Popover>
  )
}

const LinkedInButton = ({ link }) => (
  <TealToolTip title='Share with Linkedin' placement='top' arrow={false} bgColor='dark'>
    <button className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue' aria-label='Share on Linkedin' data-event-category='Article' data-event-action='Share' data-event-label='LinkedIn'>
      <LinkedinShareButton url={link}>
        <LinkedinIcon size={32} windowWidth={750} windowHeight={600} round />
      </LinkedinShareButton>
    </button>
  </TealToolTip>
)

export const CopyLinkToClipboardButton = ({ link, size = 'medium', dataClipboardText, toolTipPlacement = 'top' }) => (
  <TealToolTip title='Copy link to clipboard' placement={toolTipPlacement} arrow={false} bgColor='dark'>
    <button
      className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue'
      aria-label='Copy to clipboard'
      data-clipboard-text={dataClipboardText}
      data-event-category='Article'
      data-event-action='Share'
      data-event-label='Clipboard'
      onClick={() => {
        // clipboard js does not work when element is rendered in a Popover component
        navigator.clipboard.writeText(dataClipboardText)
        notificationToast(<Link size={20} />, toast.TYPE.INFO, 'Link copied to clipboard')
      }}
    >
      <Link size={20} />
    </button>
  </TealToolTip>
)

const PrintArticleButton = ({ pdfUrl }) => {
  const handleOnClick = () => {
    const doc = window.open(pdfUrl)
    doc.print()
  }

  return (
    <TealToolTip title='Print' placement='top' arrow={false} bgColor='dark'>
      <button
        className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue'
        aria-label='Print'
        onClick={handleOnClick}
        data-event-category='Article'
        data-event-action='Print'
      >
        <Icon name='icon-printer-variant' />
      </button>
    </TealToolTip>
  )
}

const DownloadPdfButton = ({ pdfUrl }) => (
  <TealToolTip title='Download PDF' placement='top' arrow={false} bgColor='dark'>
    <a
      href={pdfUrl}
      download
      className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue'
      aria-label='Download Pdf'
      data-event-category='Article'
      data-event-action='Download'
      onClick={() => toast('PDF Downloading')}
    >
      <Icon name='icon-pdf' />
    </a>
  </TealToolTip>
)

const SendEmailButton = ({ articleLink }) => {
  const subject = 'FromCounsel - Shared content'
  const body = `A colleague has shared some FromCounsel content with you: ${articleLink}`
  const emailLink = 'mailto:' + mailParser.reverse({ subject, body }).replace('#', '%23')

  return (
    <TealToolTip title='Send Email' placement='top' arrow={false} bgColor='dark'>
      <button
        className='outline-0 pointer pa0 ba-0 bg-transparent cadet-blue'
        onClick={() => window.open(emailLink, '_self')}
        aria-label='Email'
        data-event-category='Article'
        data-event-action='Share'
        data-event-label='Email'
      >
        <Icon name='icon-email-variant' />
      </button>
    </TealToolTip>
  )
}

const IconButtons = ({ isLogged, stateSource, classNames, aol, title }) => {
  if (!isLogged) return null

  const stateData = useObservable(stateSource, { short: null }, [title])
  const { short } = stateData
  const articleLink = url(SHARE, { aol, short }, {}, true)
  const pdfUrl = (isValidResponse(short) && getBaseURI() + new Route(URL_PDF_CA).reverse({ aol, short })) || null

  return (
    <div className={cx('flex article-page-icons cadet-blue', classNames)}>
      <ShareButton articleTitle={htmlText(title)} articleLink={articleLink} />
      <SendEmailButton articleLink={articleLink} />
      <PrintArticleButton pdfUrl={pdfUrl} />
      <DownloadPdfButton pdfUrl={pdfUrl} />
    </div>
  )
}

export default IconButtons
